import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic2, Upload, Trash2, Play, Loader2, Power, Radio } from "lucide-react";
import { toast } from "sonner";
import { DAYS } from "./musicApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/music/voice`;
const hdr = () => { const h = {}; const t = localStorage.getItem("admin_token"); if (t) h.Authorization = `Bearer ${t}`; const pin = localStorage.getItem("checkin_pin"); if (pin) h["X-HK-PIN"] = pin; return { headers: h }; };
const ZONES = [["piscina", "Piscina"], ["ristorante", "Ristorante"]];

function UploadForm({ onDone }) {
  const [f, setF] = useState({ title: "", zones: ["ristorante"], mode: "jingle", every_tracks: 8, times: "", days: [] });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Scegli il file MP3 scaricato da Artlist");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", f.title || file.name.replace(/\.[^.]+$/, ""));
    fd.append("zones", f.zones.join(","));
    fd.append("mode", f.mode);
    fd.append("every_tracks", String(f.every_tracks));
    fd.append("times", f.times.split(/[,\s]+/).filter(Boolean).join(","));
    fd.append("days", f.days.join(","));
    setBusy(true);
    try {
      await axios.post(API, fd, hdr());
      toast.success("Annuncio caricato: la stazione lo scarica entro un minuto");
      setFile(null); setF((s) => ({ ...s, title: "", times: "" }));
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore caricamento"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-xl border border-dashed border-[#C48B31]/60 bg-[#fffaf3] p-3 space-y-3" data-testid="voice-upload-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="flex items-center gap-2 h-10 px-3 rounded-xl bg-white border border-[#e8dcc8] text-sm cursor-pointer">
          <Upload className="h-4 w-4 text-[#C48B31]" /><span className="truncate">{file ? file.name : "Scegli MP3 (voce da Artlist)"}</span>
          <input type="file" accept="audio/*,.mp3" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="voice-file" />
        </label>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titolo (es. Benvenuto piscina)" className="h-10 rounded-xl border border-[#e8dcc8] px-3 text-sm" data-testid="voice-title" />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-xs text-[#7c5a4e]">Zone:</span>
        {ZONES.map(([id, name]) => <button type="button" key={id} onClick={() => toggle("zones", id)} className={`px-3 h-8 rounded-full border text-xs font-bold ${f.zones.includes(id) ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "bg-white border-[#e8dcc8] text-[#7c5a4e]"}`} data-testid={`voice-zone-${id}`}>{name}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="inline-flex items-center gap-1"><input type="radio" checked={f.mode === "jingle"} onChange={() => setF({ ...f, mode: "jingle" })} data-testid="voice-mode-jingle" /> Jingle: ogni</label>
        <input type="number" min={2} max={50} value={f.every_tracks} onChange={(e) => setF({ ...f, every_tracks: +e.target.value })} disabled={f.mode !== "jingle"} className="w-16 h-8 rounded-lg border border-[#e8dcc8] px-2 text-sm" data-testid="voice-every" /> brani
        <label className="inline-flex items-center gap-1 ml-2"><input type="radio" checked={f.mode === "scheduled"} onChange={() => setF({ ...f, mode: "scheduled" })} data-testid="voice-mode-scheduled" /> A orario:</label>
        <input value={f.times} onChange={(e) => setF({ ...f, times: e.target.value })} disabled={f.mode !== "scheduled"} placeholder="19:30, 12:45" className="w-36 h-8 rounded-lg border border-[#e8dcc8] px-2 text-sm" data-testid="voice-times" />
      </div>
      <div className="flex flex-wrap items-center gap-1 text-xs">
        <span className="text-[#7c5a4e] mr-1">Giorni (vuoto = tutti):</span>
        {DAYS.map((d, i) => <button type="button" key={d} onClick={() => toggle("days", i)} className={`w-9 h-7 rounded-md border ${f.days.includes(i) ? "bg-[#C48B31] text-white border-[#C48B31]" : "bg-white border-[#e8dcc8] text-[#7c5a4e]"}`} data-testid={`voice-day-${i}`}>{d}</button>)}
      </div>
      <button disabled={busy} className="h-10 px-4 rounded-xl bg-[#4a1c17] text-white text-sm font-bold inline-flex items-center gap-2" data-testid="voice-upload-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Carica annuncio</button>
    </form>
  );
}

function ClipRow({ c, onChanged }) {
  const audio = useRef(null);
  const [playing, setPlaying] = useState(false);
  const play = async () => {
    if (playing) { audio.current?.pause(); setPlaying(false); return; }
    try {
      const { data } = await axios.get(`${API}/${c.id}/audio`, { ...hdr(), responseType: "blob" });
      audio.current = new Audio(URL.createObjectURL(data));
      audio.current.onended = () => setPlaying(false);
      await audio.current.play(); setPlaying(true);
    } catch { toast.error("Anteprima non disponibile"); }
  };
  const patch = (b) => axios.patch(`${API}/${c.id}`, b, hdr()).then(onChanged).catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
  const now = () => axios.post(`${API}/${c.id}/now`, {}, hdr()).then((r) => toast.success(`In onda tra pochi secondi: ${r.data.zones.map((z) => ZONES.find((x) => x[0] === z)?.[1] || z).join(", ")}`)).catch(() => toast.error("Stazione non raggiungibile"));
  const del = () => { if (window.confirm(`Eliminare "${c.title}"?`)) axios.delete(`${API}/${c.id}`, hdr()).then(onChanged); };
  const when = c.mode === "jingle" ? `ogni ${c.every_tracks} brani` : `alle ${c.times.join(", ")}`;
  const days = c.days?.length ? c.days.map((d) => DAYS[d]).join(" ") : "tutti i giorni";
  return (
    <div className={`flex items-center gap-3 rounded-xl border p-3 ${c.enabled ? "bg-white border-[#e8dcc8]" : "bg-[#f7f2ec] border-[#e8dcc8] opacity-60"}`} data-testid={`voice-clip-${c.id}`}>
      <button onClick={play} className="h-9 w-9 rounded-full bg-[#4a1c17] text-white grid place-items-center shrink-0" data-testid={`voice-play-${c.id}`}><Play className="h-4 w-4" /></button>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm truncate">{c.title}</div>
        <div className="text-xs text-[#7c5a4e] truncate">{c.zones.map((z) => ZONES.find(([id]) => id === z)?.[1]).join(" · ")} — {when} — {days}</div>
      </div>
      <button onClick={now} title="Trasmetti adesso nelle sue zone" className="h-8 px-2.5 rounded-full bg-[#C48B31] text-white text-xs font-bold inline-flex items-center gap-1 shrink-0" data-testid={`voice-now-${c.id}`}><Radio className="h-3.5 w-3.5" /> Trasmetti ora</button>
      <button onClick={() => patch({ enabled: !c.enabled })} title={c.enabled ? "Disattiva" : "Attiva"} className={`p-2 rounded-md ${c.enabled ? "text-emerald-700 hover:bg-emerald-50" : "text-[#7c5a4e] hover:bg-white"}`} data-testid={`voice-toggle-${c.id}`}><Power className="h-4 w-4" /></button>
      <button onClick={del} className="p-2 rounded-md text-red-600 hover:bg-red-50" data-testid={`voice-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export function VoicePanel() {
  const [clips, setClips] = useState(null);
  const load = () => axios.get(API, hdr()).then((r) => setClips(r.data)).catch(() => setClips([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 space-y-3" data-testid="music-voice">
      <div className="font-bold text-sm inline-flex items-center gap-2"><Mic2 className="h-4 w-4 text-[#C48B31]" /> Voce e annunci</div>
      <div className="text-xs text-[#7c5a4e]">Genera la voce su Artlist (voce "Elisa", lingua Italian), scarica l'MP3 e caricalo qui. La stazione lo inserisce nella musica con dissolvenza: come jingle ogni tot brani, oppure a orari fissi.</div>
      <UploadForm onDone={load} />
      {clips === null ? <Loader2 className="h-5 w-5 animate-spin text-[#C48B31]" /> : clips.length === 0 ? <div className="text-sm text-[#7c5a4e]">Nessun annuncio ancora.</div> :
        <div className="space-y-2">{clips.map((c) => <ClipRow key={c.id} c={c} onChanged={load} />)}</div>}
    </div>
  );
}
